import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useOnboarding } from '../../hooks/onboarding';
import OnboardingSpotlight from './OnboardingSpotlight';
import OnboardingTooltip from './OnboardingTooltip';
import RequiredActionHighlight from './RequiredActionHighlight';

const OnboardingManager: React.FC = () => {
  const {
    isOnboardingActive,
    isSpotlightActive,
    currentStep,
    steps,
    targetSelector,
    tooltipPosition,
    isLoading,
    completeStep,
    closeModal,
    skipTutorial,
    completeTutorial,
    markActionCompleted,
  } = useOnboarding();

  // Track whether required actions are in focus
  const [highlightedAction, setHighlightedAction] = useState<string | null>(
    null,
  );

  const [continueEnabled, setContinueEnabled] = useState(true);

  const isLastStep = useMemo(() => {
    if (!currentStep || !steps.length) return false;
    return currentStep.order === steps[steps.length - 1].order;
  }, [currentStep, steps]);

  const isValidSelector = useCallback((selector: string): boolean => {
    try {
      document.querySelector(selector);
      return true;
    } catch {
      return false;
    }
  }, []);

  // Determine which action to highlight
  useEffect(() => {
    if (!currentStep || isLoading) {
      setHighlightedAction(null);
      setContinueEnabled(true);
      return;
    }

    // Encontrar ações válidas
    // Verificar se as ações requeridas incluem apenas o botão de continuar
    const requiredActions = currentStep.requiredActions || [];
    const filteredActions = requiredActions.filter(
      action => action.trim() !== '' && isValidSelector(action),
    );

    // Se a única ação requerida for o botão de continuar, não precisamos destacá-lo
    const hasOnlyContinueButton =
      filteredActions.length === 1 &&
      filteredActions[0] === '.onboarding-continue-button';

    // Se não tiver ações ou só tiver o botão de continuar, habilite o botão
    if (filteredActions.length === 0 || hasOnlyContinueButton) {
      setHighlightedAction(null);
      setContinueEnabled(true);
      return;
    }

    // Para outras ações que não sejam o botão de continuar
    const actionsToHighlight = filteredActions.filter(
      action => action !== '.onboarding-continue-button',
    );

    if (actionsToHighlight.length > 0) {
      const validAction = actionsToHighlight[0];
      setHighlightedAction(validAction);
      setContinueEnabled(false);
    } else {
      setHighlightedAction(null);
      setContinueEnabled(true);
    }
  }, [currentStep, isLoading, isValidSelector]);

  // Setup event listeners for required actions
  useEffect(() => {
    if (!highlightedAction || !currentStep || isLoading) {
      return undefined;
    }

    const elementsWithListeners: Element[] = [];

    const handleActionClick = (): void => {
      markActionCompleted(currentStep.id, highlightedAction);
      setHighlightedAction(null);
      setContinueEnabled(true);
    };

    const maxAttempts = 10;
    let attempts = 0;

    const tryFindElements = (): void => {
      if (attempts >= maxAttempts) {
        setContinueEnabled(true);
        return;
      }

      attempts += 1;

      try {
        const actionElements = document.querySelectorAll(highlightedAction);

        if (actionElements.length === 0) {
          setTimeout(tryFindElements, 100);
          return;
        }

        // Limpar listeners anteriores
        elementsWithListeners.forEach(el => {
          el.removeEventListener('click', handleActionClick);
        });
        elementsWithListeners.length = 0;

        // Adicionar novos listeners
        actionElements.forEach(element => {
          element.addEventListener('click', handleActionClick);
          elementsWithListeners.push(element);
        });
      } catch {
        setContinueEnabled(true);
      }
    };

    tryFindElements();

    return () => {
      elementsWithListeners.forEach(el => {
        el.removeEventListener('click', handleActionClick);
      });
    };
  }, [currentStep, highlightedAction, isLoading, markActionCompleted]);

  const handleComplete = useCallback((): void => {
    if (!currentStep) return;

    if (isLastStep) {
      completeTutorial();
    } else {
      completeStep(currentStep.id);
    }
  }, [currentStep, isLastStep, completeTutorial, completeStep]);

  // Perform action when continue is clicked (if possible)
  const handleContinue = useCallback((): void => {
    if (!currentStep) return;

    if (highlightedAction) {
      // Try to click the highlighted element if it's not the continue button
      const actionElement = document.querySelector(
        highlightedAction,
      ) as HTMLElement;
      if (actionElement && typeof actionElement.click === 'function') {
        actionElement.click();
      }
    } else {
      // Se não há ação destacada, mas o passo requer a ação do botão continuar
      const requiredActions = currentStep.requiredActions || [];
      const continueButtonRequired = requiredActions.includes(
        '.onboarding-continue-button',
      );

      if (continueButtonRequired) {
        // Marca o botão de continuar como uma ação concluída
        markActionCompleted(currentStep.id, '.onboarding-continue-button');
      }

      // Avança para o próximo passo
      handleComplete();
    }
  }, [currentStep, highlightedAction, handleComplete, markActionCompleted]);

  // Don't render anything if the onboarding isn't active
  if (!isOnboardingActive || !currentStep) return null;

  return (
    <>
      {isSpotlightActive && (
        <OnboardingSpotlight
          targetSelector={targetSelector}
          isActive={isOnboardingActive}
          onOverlayClick={() => {}} // do nothing
          padding={1}
          pulseAnimation
          shape={targetSelector?.includes('avatar') ? 'circle' : 'rectangle'}
        />
      )}

      {highlightedAction && (
        <RequiredActionHighlight
          selector={highlightedAction}
          isActive={isOnboardingActive}
        />
      )}

      <OnboardingTooltip
        title={currentStep.title}
        description={currentStep.description}
        targetSelector={targetSelector}
        position={tooltipPosition}
        isActive={isOnboardingActive}
        onComplete={handleContinue}
        onClose={closeModal}
        onSkip={skipTutorial}
        isLastStep={isLastStep}
        isButtonEnabled={continueEnabled}
        requiredActionActive={!!highlightedAction}
      />
    </>
  );
};

export default OnboardingManager;
